import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import BrandingsService, { IBrandingSpotApi } from '@services/BrandingsService';
import MapTemplateService from '@services/MapTemplateService';
import {
  BrandingDriftState,
  computeBrandingSpotDrift,
  computeTriggerDrift,
  IBrandingDrift,
  ITriggerDrift,
  pickPreviousTemplate,
} from '@shared/utils/mapTemplateDrift';
import type { IMapTemplateRecord } from '@shared/interfaces/mapTemplate';
import type { IAssistantScript } from '@shared/interfaces/assistantScript';

import { useEditorAiData } from './AiScripts/EditorAiDataContext';

export interface DriftRollup {
  state: BrandingDriftState;
  missingSpots: number;
  orphanedSpots: number;
  newScripts: number;
  orphanedScripts: number;
  hasDrift: boolean;
}

export interface EditorDriftData {
  loading: boolean;
  hasTemplate: boolean;
  templateVersion?: string;
  spotDrift: IBrandingDrift<IBrandingSpotApi>;
  triggerDrift: ITriggerDrift<IAssistantScript>;
  rollup: DriftRollup;
  refetchTemplates: () => Promise<void>;
  refreshSpots: () => Promise<void>;
}

const EditorDriftContext = createContext<EditorDriftData | null>(null);

interface DriftProviderProps {
  children: ReactNode;
}

export const DriftProvider = ({ children }: DriftProviderProps) => {
  const { currentRoom, currentBrandingId, brandingSpots, setBrandingSpots } =
    useMytaverseEvent();
  const { scripts } = useEditorAiData();

  const mapId = currentRoom?.map?.mapId;
  const mapName = currentRoom?.map?.name;

  const [templates, setTemplates] = useState<IMapTemplateRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const refetchTemplates = useCallback(async () => {
    if (!mapId) {
      setTemplates([]);
      return;
    }
    setLoading(true);
    try {
      const response = await MapTemplateService.getMapTemplates(mapId, {
        limit: 50,
      });
      setTemplates(response.mapTemplates ?? []);
    } catch (error) {
      console.error(
        `Failed to load map templates for drift: ${(error as Error).message}`,
        { mapId },
      );
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [mapId]);

  useEffect(() => {
    void refetchTemplates();
  }, [refetchTemplates]);

  const activeTemplate = useMemo(
    () => templates.find((template) => template.status === 'ACTIVE') ?? null,
    [templates],
  );
  const previousTemplate = useMemo(
    () => pickPreviousTemplate(templates, activeTemplate),
    [templates, activeTemplate],
  );

  const spots = useMemo<IBrandingSpotApi[]>(
    () => (Array.isArray(brandingSpots) ? brandingSpots : []),
    [brandingSpots],
  );

  const spotDrift = useMemo(
    () =>
      computeBrandingSpotDrift<IBrandingSpotApi>(
        spots,
        activeTemplate,
        previousTemplate,
      ),
    [spots, activeTemplate, previousTemplate],
  );

  // Match the AI tree's scope filter (aggregate.ts → filterByScope) so the
  // drift groups agree with the Scripted / No-script groups already rendered.
  const scopedScripts = useMemo(
    () =>
      scripts.filter((script) => {
        if (mapName && script.mapName !== mapName) {
          return false;
        }
        if (currentBrandingId && script.brandingId !== currentBrandingId) {
          return false;
        }
        return true;
      }),
    [scripts, mapName, currentBrandingId],
  );

  const triggerDrift = useMemo(
    () =>
      computeTriggerDrift<IAssistantScript>(
        scopedScripts,
        activeTemplate,
        previousTemplate,
      ),
    [scopedScripts, activeTemplate, previousTemplate],
  );

  const refreshSpots = useCallback(async () => {
    if (!currentBrandingId) {
      return;
    }
    try {
      const refreshed = await BrandingsService.getBrandingSpots(
        currentBrandingId,
        { limit: 1000 },
      );
      setBrandingSpots(refreshed.spots ?? []);
    } catch (error) {
      console.error(
        `Failed to refresh branding spots for drift: ${
          (error as Error).message
        }`,
        { brandingId: currentBrandingId },
      );
    }
  }, [currentBrandingId, setBrandingSpots]);

  const hasTemplate = Boolean(activeTemplate);

  const rollup = useMemo<DriftRollup>(() => {
    let state: BrandingDriftState;
    if (!hasTemplate) {
      state = 'unknown';
    } else if (spotDrift.orphaned > 0 || triggerDrift.orphaned > 0) {
      state = 'outdated';
    } else if (spotDrift.missing > 0 || triggerDrift.newCount > 0) {
      state = 'behind';
    } else {
      state = 'up-to-date';
    }
    return {
      state,
      missingSpots: spotDrift.missing,
      orphanedSpots: spotDrift.orphaned,
      newScripts: triggerDrift.newCount,
      orphanedScripts: triggerDrift.orphaned,
      hasDrift: state === 'behind' || state === 'outdated',
    };
  }, [hasTemplate, spotDrift, triggerDrift]);

  const value = useMemo<EditorDriftData>(
    () => ({
      loading,
      hasTemplate,
      templateVersion: activeTemplate?.version,
      spotDrift,
      triggerDrift,
      rollup,
      refetchTemplates,
      refreshSpots,
    }),
    [
      loading,
      hasTemplate,
      activeTemplate,
      spotDrift,
      triggerDrift,
      rollup,
      refetchTemplates,
      refreshSpots,
    ],
  );

  return (
    <EditorDriftContext.Provider value={value}>
      {children}
    </EditorDriftContext.Provider>
  );
};

export const useDrift = (): EditorDriftData => {
  const ctx = useContext(EditorDriftContext);
  if (!ctx) {
    throw new Error('useDrift must be used inside DriftProvider');
  }
  return ctx;
};
